import {
  Box, Button,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Paper,
  TextField, Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import * as React from 'react';
import useSWR from 'swr';
import CloseIcon from '@material-ui/icons/Close';

import { useSnackbar } from 'notistack';
import ChipSelect from '../ChipSelect';
import type {
  IRepairGrouping, IJobGroupingProps,
} from './MassReceiveInterfaces';
import type {
  IRepairJob, IFieldOptions, IFields,
} from '../estimator/EstimatorInterfaces';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */

const useStyles = makeStyles(() => createStyles({
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
}));

const JobGrouping: React.FC<IJobGroupingProps> = (
  {
    addNewRepairGrouping, groupNumber, grouping, elemDisabled = false,
  },
) => {
  const classes = useStyles();
  const [numberOfItems, setNumberOfItems] = React.useState<number>(grouping?.numberOfItems || 0);
  const [startingBarcode, setStartingBarcode] = React.useState<number>(grouping?.startingBarcode || 0);
  const [endingBarcode, setEndingBarcode] = React.useState<number>(grouping?.endingBarcode || 0);
  const [jobsAssigned, setJobsAssigned] = React.useState<IRepairJob[]>(grouping?.jobsAssigned || []);
  const [disableAdd, setDisableAdd] = React.useState<boolean>(true);
  const [repairJob, setRepairJob] = React.useState<IRepairJob>();
  const [selectedFieldOptions, setSelectedFieldOptions] = React.useState<Array<IFieldOptions>>(new Array(repairJob?.fields.length));

  const fetcher = (url: string) => axios.get<IRepairJob[]>(url)
    .then((response) => response.data);

  const { data: repairJobs } = useSWR<IRepairJob[]>(
    ['/estimator/getRepairJobs'],
    (url) => fetcher(url),
  );

  React.useEffect(() => {
    if (numberOfItems > 0 && startingBarcode > 0) {
      // if able, set ending barcode, inclusive
      setEndingBarcode((startingBarcode + numberOfItems) - 1);

      // check the rest of the fields
      if (endingBarcode > 0 && jobsAssigned.length > 0) {
        setDisableAdd(false);
      }
    }
  }, [numberOfItems, startingBarcode, endingBarcode, jobsAssigned]);

  const { enqueueSnackbar } = useSnackbar();

  const resetStateValues = () => {
    setNumberOfItems(0);
    setStartingBarcode(0);
    setEndingBarcode(0);
    setJobsAssigned([]);
    setDisableAdd(true);
  };

  const handleSelectedRepairJobChange = (selectedRepairJob: IRepairJob | null) => {
    if (selectedRepairJob !== null) {
      axios.get<IRepairJob>('/estimator/getRepairJob', {
        params: {
          repairJobId: selectedRepairJob.id,
        },
      })
        .then((response) => {
          setRepairJob(response.data);
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    } else {
      setRepairJob(undefined);
    }
  };

  const validJobGrouping = (
    numberOfItems > 0
    && /^\d{10}$/.test(String(startingBarcode))
    && /^\d{10}$/.test(String(endingBarcode))
    && jobsAssigned.length > 0
  );

  const handleAddNewRepairGroupingClick = () => {
    if (
      validJobGrouping
    ) {
      const newRepairGrouping: IRepairGrouping = {
        groupNumber,
        numberOfItems,
        startingBarcode,
        endingBarcode,
        jobsAssigned,
      };

      resetStateValues();

      addNewRepairGrouping(newRepairGrouping);
    }
  };

  const handleFieldOptionsChange = (idx: number, selectedOption: IFieldOptions | null) => {
    if (selectedOption) {
      const updatedFieldOptions = [...selectedFieldOptions];
      updatedFieldOptions[idx] = selectedOption;
      setSelectedFieldOptions(updatedFieldOptions);
    }
  };

  const filteredJobs = repairJobs?.filter((job) => jobsAssigned.findIndex((ja) => ja.id === job.id) === -1);

  return (
    <Box>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box pl={2}>
              <Typography className={classes.title}>{`Job Grouping ${groupNumber}`}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={4}>
            <Box pl={2}>
              <TextField
                required
                type="number"
                value={numberOfItems > 0 ? numberOfItems : ''}
                label="# of Items"
                id="number-of-items-input"
                variant="outlined"
                fullWidth
                onChange={(event) => setNumberOfItems(+event.target.value)}
                disabled={elemDisabled}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <TextField
              required
              type="number"
              value={startingBarcode > 0 ? startingBarcode : ''}
              id="starting-barcode-input"
              label="Starting Barcode"
              error={startingBarcode ? !/^\d{10}$/.test(String(startingBarcode)) : false}
              helperText={startingBarcode && !/^\d{10}$/.test(String(startingBarcode)) ? 'Barcode must be 10 digits' : null}
              variant="outlined"
              fullWidth
              onChange={(event) => setStartingBarcode(+event.target.value)}
              disabled={elemDisabled}
            />
          </Grid>
          <Grid item xs={4}>
            <Box pr={2}>
              <TextField
                required
                type="number"
                value={endingBarcode > 0 ? endingBarcode : ''}
                id="ending-barcode-input"
                label="Ending Barcode"
                variant="outlined"
                fullWidth
                disabled={elemDisabled}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box px={2}>
              <Autocomplete
                onChange={(event: unknown, selectedJob: IRepairJob | null) => {
                  handleSelectedRepairJobChange(selectedJob);
                }}
                autoHighlight
                autoSelect
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`${filteredJobs?.toString()}-${repairJob}`}
                value={repairJob}
                options={filteredJobs || []}
                getOptionLabel={(option) => option.name.toString()}
                renderInput={(params) => (
                  <TextField
                    required
                    // prop spreading is directly from MUI tsx example
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    variant="outlined"
                    id="repair-jobs-input"
                    label="Repair Job"
                    fullWidth
                  />
                )}
                disabled={elemDisabled}
              />
            </Box>
          </Grid>
          {repairJob && repairJob.fields.length > 0 && (
            <Grid item xs={12}>
              <Box pl={2} mt={2} mb={2}>
                <Typography variant="h6">{`${repairJob.name} Data`}</Typography>
              </Box>
              <Grid container justifyContent="flex-start">
                {repairJob.fields.map((field, i) => (
                  <Grid item xs={12} md={6}>
                    <Box pl={2} m={1}>
                      <Autocomplete
                        value={selectedFieldOptions[i]}
                        onChange={(event: unknown, selectedOption: IFieldOptions | null) => {
                          handleFieldOptionsChange(i, selectedOption);
                        }}
                        autoHighlight
                        autoSelect
                        options={field.fieldOptions}
                        getOptionLabel={(option) => option?.name.toString() || ''}
                        // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => <TextField {...params} label={`Select ${field.name}`} variant="outlined" />}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
          <Grid item container direction="row" justifyContent="flex-end" xs={12}>
            <Grid item>
              <Box pr={2}>
                <Button
                  fullWidth
                  disabled={repairJob?.fields?.length
                    ? selectedFieldOptions.filter((option) => (option?.selected || option?.id)).length < (repairJob?.fields?.length || 1)
                    : Boolean(!repairJob)}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (repairJob) {
                      const newRepairJob = repairJob;
                      newRepairJob.fields.forEach((field: IFields, idx: number) => {
                        // eslint-disable-next-line no-param-reassign
                        field.fieldOptions = [selectedFieldOptions[idx]];
                      });
                      setJobsAssigned([...jobsAssigned, newRepairJob]);
                      setSelectedFieldOptions([]);
                      setRepairJob(undefined);
                    }
                  }}
                >
                  {'Add Repair Job'}
                </Button>
              </Box>
            </Grid>
          </Grid>
          {jobsAssigned.length > 0
            && (
              <Grid item xs={12}>
                <Box pl={2}>
                  <Typography className={classes.title}>{'Selected Repair Jobs'}</Typography>
                </Box>
              </Grid>
            )}
          <Grid item container xs={12}>
            {jobsAssigned.length > 0 && jobsAssigned.map((job) => (
              <Grid item>
                <Box pl={2} pt={1}>
                  <ChipSelect
                    key={job.id}
                    label={job.name}
                    selected
                    customIcon={<CloseIcon />}
                    handleClick={() => {
                      setJobsAssigned(jobsAssigned.filter((x) => x.id !== job.id));
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Grid item container direction="row" justifyContent="flex-end" xs={12}>
            <Grid item>
              <Box pr={2}>
                <Button
                  disabled={disableAdd || !validJobGrouping}
                  fullWidth
                  id="add-new-grouping-button"
                  variant="contained"
                  color="primary"
                  onClick={() => handleAddNewRepairGroupingClick()}
                >
                  {grouping ? 'UPDATE GROUPING' : 'ADD NEW GROUPING'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default JobGrouping;
